import { apiDownloadFile, apiRequest, apiUpload } from '@/api/client';
import type {
  AuthenticatedUser,
  EmailVerificationInput,
  LoginResult,
  MessageResult,
  PasswordResetInput,
  RegistrationOtpRequestInput,
  RegistrationOtpVerifyInput,
  RegistrationOtpVerifyResult,
  RegistrationInput,
  UpdatePasswordInput,
  UpdateProfileInput,
} from '@/api/types';

export function login(email: string, password: string) {
  return apiRequest<LoginResult>('/auth/login', {
    method: 'POST',
    body: { email, password },
  });
}

export function logout() {
  return apiRequest<null>('/auth/logout', { method: 'POST' });
}

export function fetchCurrentUser() {
  return apiRequest<AuthenticatedUser>('/auth/me');
}

export function updateProfile(input: UpdateProfileInput) {
  return apiRequest<AuthenticatedUser>('/auth/me', {
    method: 'PATCH',
    body: input,
  });
}

export function updatePassword(input: UpdatePasswordInput) {
  return apiRequest<MessageResult>('/auth/password', {
    method: 'PUT',
    body: input,
  });
}

/** Deactivates the signed-in user's own account (requires their password). */
export function deactivateAccount(currentPassword: string) {
  return apiRequest<{ deactivated: boolean }>('/auth/me/deactivate', {
    method: 'POST',
    body: { current_password: currentPassword },
  });
}

export function uploadProfilePhoto(file: File) {
  const formData = new FormData();
  formData.append('profile_photo', file);

  return apiUpload<AuthenticatedUser>('/auth/me/profile-photo', formData);
}

export function downloadProfilePhoto() {
  return apiDownloadFile('/auth/me/profile-photo');
}

export function deleteProfilePhoto() {
  return apiRequest<AuthenticatedUser>('/auth/me/profile-photo', { method: 'DELETE' });
}

export function requestRegistrationOtp(input: RegistrationOtpRequestInput) {
  return apiRequest<MessageResult>('/auth/register/email-otp', {
    method: 'POST',
    body: input,
  });
}

export function verifyRegistrationOtp(input: RegistrationOtpVerifyInput) {
  return apiRequest<RegistrationOtpVerifyResult>('/auth/register/email-otp/verify', {
    method: 'POST',
    body: input,
  });
}

export function register(input: RegistrationInput) {
  return apiRequest<MessageResult>('/auth/register', {
    method: 'POST',
    body: input,
  });
}

export function requestPasswordReset(email: string) {
  return apiRequest<MessageResult>('/auth/forgot-password', {
    method: 'POST',
    body: { email },
  });
}

export function resendPasswordReset(email: string) {
  return apiRequest<MessageResult>('/auth/forgot-password/resend', {
    method: 'POST',
    body: { email },
  });
}

export function resetPassword(input: PasswordResetInput) {
  return apiRequest<MessageResult>('/auth/reset-password', {
    method: 'POST',
    body: input,
  });
}

export function resendEmailVerification(email: string) {
  return apiRequest<MessageResult>('/auth/email/verification-notification', {
    method: 'POST',
    body: { email },
  });
}

export function verifyEmail(input: EmailVerificationInput) {
  return apiRequest<MessageResult>('/auth/verify-email', {
    method: 'POST',
    body: input,
  });
}
